import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Table from '../../../../components/Table';

// API
import {
    addBatteriesAPI,
    deleteBatteriesAPI,
    editBatteriesAPI,
    getAllVendorsAPI,
    getMasterBatteriesAPI,
    getStationNameAPI,
    registerDeviceMobius,
    startAndStopAlertAPI,
} from '../../../../API/RDPMS_API';

// Utils
import moment from 'moment';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from 'react-redux';
import { capitalize } from '../../../../utils/statusUtils';
import { capitalizeFirstLetter } from '../../../../constants/Uppercase';
import { addBatteriesColumns, sizePerPageList } from '../../../../utils/ColumnUtils';

// SCSS
import '../../../../../src/assets/scss/custom/components/_addBatteries.scss';

// Bootstrap Instance
import * as bootstrap from 'bootstrap';

// Reusable Master Modals
import AddMasterModal from '../../../../components/modal/AddMasterModal';
import EditMasterModal from '../../../../components/modal/EditMasterModal';
import DeleteMasterModal from '../../../../components/modal/DeleteMasterModal';

// Loading (keep last)
import { Loading } from 'react-loading-ui';

const AddBatteries = () => {
    const { t } = useTranslation();
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [vendor, setVendor] = useState([]);
    const [limit, setLimit] = useState(5);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [stationName, setStationName] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showEditModalData, setShowEditModalData] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [batteryToDelete, setBatteryToDelete] = useState(null);
    const [selectedToDeleteDeviceId, setSelectedToDeleteDeviceId] = useState(null);

    const { auth } = useSelector((state) => ({ auth: state.Auth }));
    let stationIds = auth?.user?.stationId;
    let userId = auth?.user?._id;
    const userRole = capitalizeFirstLetter(auth?.user?.role || 'Admin');

    //  Fetch all master data
    const fetchAllData = useCallback(async () => {
        try {
            setLoader(true);
            await Promise.all([getMasterBatteriesData(limit, pageNumber), getStationNameData(), getAllVendorsData()]);
        } catch (error) {
            console.error('Error fetching all data:', error);
        } finally {
            setLoader(false);
        }
    }, [limit, pageNumber]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    //  Vendor Data
    const getAllVendorsData = async () => {
        try {
            const response = await getAllVendorsAPI();
            if (Array.isArray(response) && response.length) setVendor(response);
        } catch (error) {
            console.error('Vendor fetch error:', error);
            toast.error(t('Failed to refresh vendor data'), { position: 'top-right' });
        }
    };

    //  Station Data
    const getStationNameData = async () => {
        try {
            const response = await getStationNameAPI({ userId });
            if (Array.isArray(response) && response.length) setStationName(response);
        } catch (error) {
            console.error('Station fetch error:', error);
        }
    };

    const getMasterBatteriesData = async (limits = limit, pages = pageNumber, showNoDataPopup = true) => {
        const request = { limit: limits, page: pages, stationId: stationIds };

        try {
            const response = await getMasterBatteriesAPI(request);

            if (response?.data?.length > 0) {
                const dataArray = response.data.map((item) => ({
                    id: item._id,
                    createdDate: moment(item.updatedAt).format('Do MMM YYYY, h:mm a'),
                    batteryCharger: item.battery_charger,
                    assetTypeCode: item.asset_type_code || '',
                    assetNumberCode: item.asset_number_code || '',
                    smmsAssetCode: item.smms_asset_code,
                    smmsAssetName: item.smms_asset_name,
                    channel: item.channel || '',
                    device: item.device_id || '',
                    type: capitalize(item.type || ''),
                    stationMasterName: capitalize(item.stationDetails?.[0]?.station_master || ''),
                    stationName: item.stationDetails?.[0]?.name || '',
                    vendor: item.vendorDetails?.[0]?.vendor_cloud_name || '',
                    city: item.stationDetails?.[0]?.city || '',
                    state: item.stationDetails?.[0]?.state || '',
                    parameterTypeId: item.parameter_type_id,
                    action: (
                        <div className="action-icons d-flex justify-content-center align-items-center gap-3">
                            <Link to="#" className="action-icon" onClick={() => openEditModalWithHeaderClass(item)}>
                                <i className="mdi mdi-pencil" title="Edit"></i>
                            </Link>
                            <Link
                                to="#"
                                className="action-icon text-danger"
                                onClick={() => {
                                    setSelectedToDeleteDeviceId(item.device_id);
                                    openDeleteModal(item._id);
                                }}>
                                <i className="mdi mdi-delete" title="Delete"></i>
                            </Link>
                        </div>
                    ),
                    stopAlert: (
                        <Form>
                            <Form.Check
                                type="switch"
                                id={`custom-switch-${item._id}`}
                                checked={item.alert_status}
                                onChange={(e) => handleToggle(e, item._id)}
                            />
                        </Form>
                    ),
                }));

                setPageNumber(response.currentPage);
                setPageCount(response.totalPages);
                setData(dataArray);
                setTotalCount(response.totalCount);

                new bootstrap.Modal(document.getElementById('updateBatteries')).show();
            } else if (showNoDataPopup) {
                const modalInstance = new bootstrap.Modal(document.getElementById('alertNoDataModal'));
                modalInstance.show();
                setTimeout(() => modalInstance.hide(), 1000);
            }
        } catch (error) {
            console.error('Error fetching batteries data:', error);
            toast.error(t('Failed to fetch battery data'), {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    };

    //  Alert Toggle (Single or All)
    const handleToggle = async (e, id, isAll = false) => {
        try {
            const request = { name: 'BatteryCharger', id, status: e.target.checked, isAll, stationId: stationIds };
            const response = await startAndStopAlertAPI(request);
            if (response) getMasterBatteriesData(limit, pageNumber);
        } catch (error) {
            console.error('Toggle alert error:', error);
        }
    };

    const enableAllAlerts = async () => await handleToggle({ target: { checked: true } }, null, true);
    const disableAllAlerts = async () => await handleToggle({ target: { checked: false } }, null, true);

    //  Pagination
    const handlePageChange = (value) => {
        setLimit(value);
        setPageNumber(1);
    };

    const handlePageClick = (value) => setPageNumber(value);

    //  Modal Handlers
    const openModalWithHeaderClass = () => setShowModal(true);
    const closeModalWithHeaderClass = () => setShowModal(false);
    const openEditModalWithHeaderClass = (data) => {
        setShowEditModalData(data);
        setShowEditModal(true);
    };
    const closeEditModalWithHeaderClass = () => setShowEditModal(false);
    const openDeleteModal = (id) => {
        setBatteryToDelete(id);
        setShowDeleteModal(true);
    };
    const closeDeleteModal = () => {
        setBatteryToDelete(null);
        setShowDeleteModal(false);
    };

    const schemaResolver = yupResolver(
        yup.object().shape({
            batteryCharger: yup.string().required(t('Please Enter Battery Charger')),
            // assetTypeName: yup.string().required(t('Please Enter Asset Type Name')),
            assetTypeCode: yup.string().required(t('Please Enter Asset Type Code')),
            smmsAssetCode: yup.string().required(t('Please Enter SMMS Asset Code')),
            smmsAssetName: yup.string().required(t('Please Enter SMMS Asset Name')),
            parameterTypeId: yup.string().required(t('Please Enter Parameter ID')),
            assetTypeId: yup.string().required(t('Please Enter Asset Type ID')),
            assetNumberCode: yup.string().required(t('Please Enter Asset Number Code')),
            assetNumberId: yup.string().required(t('Please Enter Asset Number ID')),
            vendorId: yup.string().required(t('Please Select Vendor')),
            deviceId: yup.string().required(t('Please Enter Device ID')),
            channel: yup
                .number()
                .typeError(t('Please enter a valid number'))
                .required(t('Please Enter Channel Number'))
                .transform((value, originalValue) => (originalValue === '' ? undefined : value)),
            type: yup.string().required(t('Please Select Type')),
            stationId: yup.string().required(t('Please Select Station Name')),
        })
    );

    //  Submit (Add)
    const onSubmit = async (formData) => {
        try {
            setLoader(true);
            // const payload = {
            //     ...formData,
            //     type: capitalizeFirstLetter(formData.type),
            //     createdBy: userRole,
            //     updatedBy: userRole,
            //     assetTypeName: 'Battery Charger',
            // };

            const payload = {
                'm2m:cin': {
                    con: {
                        ...formData,
                        type: capitalizeFirstLetter(formData.type),
                        createdBy: userRole,
                        updatedBy: userRole,
                        assetTypeName: 'Battery Charger',
                        status: 'active',
                    },
                },
            };

            const result = await registerDeviceMobius(payload);

            if (result?.isSucess === true) {
                setTimeout(() => {
                    getMasterBatteriesData(limit, pageNumber, false);
                }, 1000);
                setShowModal(false);
                toast.success(t('Added new batteries successfully'), { position: 'top-right' });
            } else {
                toast.error(t('Please enter a different channel number'), { position: 'top-right' });
            }
        } catch (error) {
            console.error('Add error:', error);
            toast.error(t('An error occurred while adding batteries'), { position: 'top-right' });
        } finally {
            setLoader(false);
        }
    };

    //  Edit
    const onEdit = async (formData) => {
        try {
            setLoader(true);
            // const payload = {
            //     ...formData,
            //     id: showEditModalData._id,
            //     type: capitalizeFirstLetter(formData.type),
            //     updatedBy: userRole,
            //     assetTypeName: 'Battery Charger',
            // };

            // const result = await editBatteriesAPI(payload);

            const payload = {
                'm2m:cin': {
                    con: {
                        ...formData,
                        id: showEditModalData._id,
                        type: capitalizeFirstLetter(formData.type),
                        updatedBy: userRole,
                        assetTypeName: 'Battery Charger',
                        status: 'active',
                    },
                },
            };
            const result = await registerDeviceMobius(payload);

            if (result?.isSucess === true) {
                setTimeout(() => {
                    getMasterBatteriesData(limit, pageNumber, false);
                }, 1000);
                setShowEditModal(false);
                toast.success(t('Updated batteries successfully'), { position: 'top-right' });
            } else {
                toast.error(t('Please enter a different channel number'), { position: 'top-right' });
            }
        } catch (error) {
            console.error('Edit error:', error);
            toast.error(t('An error occurred while updating batteries'), { position: 'top-right' });
        } finally {
            setLoader(false);
        }
    };

    //  Delete
    const onDeleteBatteries = async () => {
        if (!batteryToDelete) return;

        try {
            // const result = await deleteBatteriesAPI(batteryToDelete);

            // if (result) {
            const payload = {
                'm2m:cin': {
                    con: {
                        id: batteryToDelete,
                        deviceId: selectedToDeleteDeviceId,
                        assetTypeName: 'Battery Charger',
                        status: 'inactive',
                    },
                },
            };
            const result = await registerDeviceMobius(payload);

            if (result?.isSucess === true) {
                setData((prev) => prev.filter((row) => row.id !== batteryToDelete));
                setTotalCount((prev) => prev - 1);
                closeDeleteModal();
                await getMasterBatteriesData();

                toast.success(t('Deleted batteries successfully'), { position: 'top-right' });
            } else {
                toast.error(t('Deletion unsuccessful'), { position: 'top-right' });
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(t('An error occurred while deleting batteries'), { position: 'top-right' });
        } finally {
            closeDeleteModal();
        }
    };

    return (
        <>
            <ToastContainer />
            <Row className="pr-4 " style={{ width: '100vw' }}>
                <Col>
                    <div className="page-title-box mb-4">
                        <div
                            className="page-title-right"
                            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Button onClick={openModalWithHeaderClass} className="export text-black">
                                {t('ADD BATTERIES')} <i className="fas fa-plus"></i>
                            </Button>
                            <button
                                className="update"
                                id="update"
                                type="button"
                                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                onClick={() => getMasterBatteriesData(limit, pageNumber)}>
                                {t('Update')}
                                <i className="mdi mdi-autorenew"></i>
                            </button>
                        </div>
                        <h4 className="btn_1 d-flex align-items-center text-nowrap text-black">{t('BATTERIES')}</h4>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            <Col className="d-flex justify-content-end align-items-center">
                                <div>
                                    <Button variant="success" style={{ marginLeft: '10px' }} onClick={enableAllAlerts}>
                                        {t('Enable All Alerts')}
                                    </Button>
                                    <Button variant="danger" style={{ marginLeft: '10px' }} onClick={disableAllAlerts}>
                                        {t('Disable All Alerts')}
                                    </Button>
                                </div>
                            </Col>
                            <div
                                className="col-6 col-sm-6 card-add-batteries p-3 border-0 text-start no-radius my-2"
                                style={{
                                    width: '100%',
                                    boxShadow: '0px 0px 35px 0px rgba(154, 161, 171, 0.15)',
                                    position: 'relative',
                                }}>
                                <div className="batteries-table-wrapper">
                                    <Table
                                        columns={addBatteriesColumns(t)}
                                        data={data}
                                        pageSize={limit}
                                        pageCount={pageCount}
                                        pageNumber={pageNumber}
                                        totalCount={totalCount}
                                        currentPage={pageNumber}
                                        sizePerPageList={sizePerPageList}
                                        isSortable
                                        pagination
                                        isSearchable
                                        onPageChange={handlePageClick}
                                        onLimitChange={handlePageChange}
                                        noDataText="No data found"
                                        loading={loader}
                                    />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <AddMasterModal
                t={t}
                title="ADD Battery Charger"
                showModal={showModal}
                closeModalWithHeaderClass={() => closeModalWithHeaderClass()}
                onSubmit={onSubmit}
                schemaResolver={schemaResolver}
                extraField={{
                    label: 'Battery Charger',
                    name: 'batteryCharger',
                    type: 'text',
                    placeholder: 'Enter Battery Charger',
                }}
                stationName={stationName}
                vendor={vendor}
                loader={loader}
            />

            <EditMasterModal
                t={t}
                title="EDIT Battery Charger"
                showModal={showEditModal}
                closeModalWithHeaderClass={() => closeEditModalWithHeaderClass()}
                onSubmit={onEdit}
                schemaResolver={schemaResolver}
                extraField={{
                    label: 'Battery Charger',
                    name: 'batteryCharger',
                    type: 'text',
                    placeholder: 'Enter Battery Charger',
                    value: showEditModalData?.battery_charger,
                }}
                showEditModalData={showEditModalData}
                stationName={stationName}
                vendor={vendor}
                loader={loader}
            />

            <DeleteMasterModal
                t={t}
                showModal={showDeleteModal}
                closeModal={() => closeDeleteModal()}
                onDelete={onDeleteBatteries}
                title="Delete Battery Charger"
                message="Are you sure you want to delete this battery charger?"
                imageSrc="/asset-images/deleteiconmodal.png"
            />

            <div
                className="modal fade"
                id="updateBatteries"
                tabIndex="-1"
                aria-labelledby="updateBatteriesLabel"
                aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header close">
                            <button
                                type="button"
                                className="btn-close ms-auto"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <div className="modal-body text-center">
                            <img
                                src="/asset-images/checkmark.png"
                                alt="Success"
                                className="img-fluid"
                                style={{ maxWidth: '100px' }}
                            />
                            <p className="mt-3">{t('Battery data refreshed successfully')}</p>
                        </div>
                    </div>
                </div>
            </div>
            {/* No Data Modal */}
            <div
                className="modal fade"
                id="alertNoDataModal"
                tabIndex="-1"
                aria-labelledby="alertNoDataModalLabel"
                aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header close">
                            <button
                                type="button"
                                className="btn-close ms-auto"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <div className="modal-body text-center">
                            <img
                                src="/asset-images/checkmark.png"
                                alt="Success"
                                className="img-fluid"
                                style={{ maxWidth: '100px' }}
                            />
                            <p className="mt-3">No Battery data available</p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddBatteries;
